import type {
    RetroActionItemDto,
    RetroBoardDto,
    RetroCardCommentedEvent,
    RetroCardDeletedEvent,
    RetroCardDto,
    RetroCardVotedEvent,
    RetroConcludedEvent,
    RetroDto,
    RetroParticipantDto,
    RetroPersonDto,
    RetroTimerState,
} from '../../../../shared/types/retro';

/** Timer plus the local time it was received, so the countdown never depends on clock skew. */
export interface ClientTimer extends RetroTimerState {
    receivedAt: number;
}

export type RetroBoard = Omit<RetroBoardDto, 'timer'> & { timer: ClientTimer };

function stampTimer(timer: RetroTimerState, now = Date.now()): ClientTimer {
    return { ...timer, receivedAt: now };
}

export function fromDto(dto: RetroBoardDto, now = Date.now()): RetroBoard {
    return { ...dto, timer: stampTimer(dto.timer, now) };
}

/** Milliseconds left on the timer at `now`. */
export function timerRemaining(timer: ClientTimer, now = Date.now()): number {
    if (!timer.running) return timer.remainingMs;
    return Math.max(0, timer.remainingMs - (now - timer.receivedAt));
}

/**
 * Pure cache updates for the retro board query, shared by live events and mutations.
 * Each returns the same object when nothing changes.
 */
type Update<T> = (board: RetroBoard, data: T) => RetroBoard;

export const upsertCard: Update<RetroCardDto> = (board, card) => {
    const exists = board.cards.some(c => c._id === card._id);
    return {
        ...board,
        cards: exists ? board.cards.map(c => (c._id === card._id ? card : c)) : [...board.cards, card],
    };
};

const PENDING_PREFIX = 'pending:';
let pendingSeq = 0;

/** A card shown the moment it's added, until the server's copy replaces it. */
export const isPendingCard = (card: Pick<RetroCardDto, '_id'>) => card._id.startsWith(PENDING_PREFIX);

export function pendingCard(board: RetroBoard, columnId: string, text: string, now = Date.now()): RetroCardDto {
    pendingSeq += 1;
    const inColumn = board.cards.filter(c => c.columnId === columnId);
    return {
        _id: `${PENDING_PREFIX}${pendingSeq}`,
        columnId,
        authorLabel: board.retro.participants.find(p => p.isSelf)?.label ?? '',
        isOwn: true,
        text,
        order: inColumn.length ? Math.max(...inColumn.map(c => c.order)) + 1 : 0,
        upvotes: 0,
        downvotes: 0,
        selfVote: 0,
        comments: [],
        createdAt: new Date(now).toISOString(),
    };
}

export const removeCard: Update<RetroCardDeletedEvent> = (board, { cardId }) => {
    if (!board.cards.some(c => c._id === cardId)) return board;
    return { ...board, cards: board.cards.filter(c => c._id !== cardId) };
};

export const applyVote: Update<RetroCardVotedEvent> = (board, { cardId, upvotes, downvotes, selfVote, votesHidden }) => ({
    ...board,
    cards: board.cards.map(c => {
        if (c._id !== cardId) return c;
        const next: RetroCardDto = { ...c, upvotes, downvotes, selfVote };
        if (votesHidden) next.votesHidden = true;
        else delete next.votesHidden;
        return next;
    }),
});

/** Optimistic vote: adjust counts locally for the caller's new vote (only the caller's own while counts are hidden). */
export const voteLocally: Update<{ cardId: string; value: 1 | -1 | 0 }> = (board, { cardId, value }) => ({
    ...board,
    cards: board.cards.map(c => {
        if (c._id !== cardId) return c;
        if (c.votesHidden) return { ...c, selfVote: value };
        let { upvotes, downvotes } = c;
        if (c.selfVote === 1) upvotes -= 1;
        if (c.selfVote === -1) downvotes -= 1;
        if (value === 1) upvotes += 1;
        if (value === -1) downvotes += 1;
        return { ...c, upvotes, downvotes, selfVote: value };
    }),
});

export const addComment: Update<RetroCardCommentedEvent> = (board, { cardId, comment }) => ({
    ...board,
    cards: board.cards.map(c => {
        if (c._id !== cardId || c.comments.some(cm => cm._id === comment._id)) return c;
        return { ...c, comments: [...c.comments, comment] };
    }),
});

export const moveCardLocally: Update<{ cardId: string; columnId: string; order: number; groupId?: string | null }> = (board, m) => ({
    ...board,
    cards: board.cards.map(c => {
        if (c._id !== m.cardId) return c;
        const next: RetroCardDto = { ...c, columnId: m.columnId, order: m.order };
        if (m.groupId !== undefined) {
            if (m.groupId === null) delete next.groupId;
            else next.groupId = m.groupId;
        }
        return next;
    }),
});

export const setRetro: Update<RetroDto> = (board, retro) => ({ ...board, retro });

/**
 * A change to the retro that changes what cards show (status, anonymity, seeing others' cards or votes), or that
 * removed a column (its cards moved to another): what's cached is out of date, so the board has to be loaded again.
 */
export function changesWhatCardsShow(before: RetroDto | undefined, after: RetroDto): boolean {
    if (!before) return false;
    const columnGone = before.columns.some(c => !after.columns.some(a => a.id === c.id));
    return columnGone || before.status !== after.status || before.isAnonymous !== after.isAnonymous
        || before.hideOthersCards !== after.hideOthersCards || before.hideOthersVotes !== after.hideOthersVotes;
}

/** Votes the viewer has cast (each card holds at most one of theirs). */
export function votesUsed(cards: RetroCardDto[]): number {
    return cards.filter(c => c.selfVote !== 0).length;
}

export const setParticipants: Update<RetroParticipantDto[]> = (board, participants) => ({
    ...board,
    retro: { ...board.retro, participants },
    joined: board.joined || participants.some(p => p.isSelf),
});

export const setPeople: Update<RetroPersonDto[]> = (board, people) => ({
    ...board,
    retro: { ...board.retro, people },
});

export const setActions: Update<RetroActionItemDto[]> = (board, actionItems) => ({
    ...board,
    retro: { ...board.retro, actionItems },
});

export const setConcluded: Update<RetroConcludedEvent> = (board, { concludedAt }) => ({
    ...board,
    retro: { ...board.retro, status: 'concluded', concludedAt },
});

export const setTimer: Update<RetroTimerState> = (board, timer) => ({ ...board, timer: stampTimer(timer) });

// ============================================================
// Grouping and layout helpers
// ============================================================

export interface DisplayItem {
    type: 'card' | 'group';
    key: string;
    order: number;
    cards: RetroCardDto[];
}

/** A card's net votes: upvotes minus downvotes. */
function cardScore(card: RetroCardDto): number {
    return card.upvotes - card.downvotes;
}

/** An item's net votes: a group counts all its cards. */
export function itemScore(item: DisplayItem): number {
    return item.cards.reduce((sum, c) => sum + cardScore(c), 0);
}

const upvotesOf = (cards: RetroCardDto[]) => cards.reduce((sum, c) => sum + c.upvotes, 0);

/** Most net votes first; ties go to more upvotes, then keep the shared order. */
function byVotes(a: { cards: RetroCardDto[]; score: number; order: number }, b: { cards: RetroCardDto[]; score: number; order: number }): number {
    return b.score - a.score || upvotesOf(b.cards) - upvotesOf(a.cards) || a.order - b.order;
}

/**
 * Cards of one column as standalone cards and groups, in display order: the shared order, or with
 * `sortByVotes` most-voted first (cards inside a group too). Sorting is a personal view; it never
 * changes the shared order.
 */
export function displayItems(cards: RetroCardDto[], sortByVotes = false): DisplayItem[] {
    const groups = new Map<string, RetroCardDto[]>();
    const items: DisplayItem[] = [];
    for (const c of cards) {
        if (c.groupId) groups.set(c.groupId, [...(groups.get(c.groupId) ?? []), c]);
        else items.push({ type: 'card', key: c._id, order: c.order, cards: [c] });
    }
    for (const [groupId, members] of groups) {
        const sorted = [...members].sort((a, b) => a.order - b.order);
        const shown = sortByVotes
            ? [...sorted].sort((a, b) => byVotes({ cards: [a], score: cardScore(a), order: a.order }, { cards: [b], score: cardScore(b), order: b.order }))
            : sorted;
        items.push({ type: 'group', key: `GROUP:${groupId}`, order: sorted[0].order, cards: shown });
    }
    if (!sortByVotes) return items.sort((a, b) => a.order - b.order);
    return items
        .map(item => ({ item, cards: item.cards, score: itemScore(item), order: item.order }))
        .sort(byVotes)
        .map(({ item }) => item);
}

/** An order value that places an item at `index` among `slots` (sorted orders of the other items). */
export function orderAt(slots: number[], index: number): number {
    if (slots.length === 0) return 0;
    if (index <= 0) return slots[0] - 1;
    if (index >= slots.length) return slots[slots.length - 1] + 1;
    return (slots[index - 1] + slots[index]) / 2;
}
